/**
 * Beta invite landing page — prospects land here from outreach emails.
 *
 * Flow:
 *   1. Fetch invite data via public /beta-invites/:slug endpoint (no auth needed).
 *   2. If signed out → "Get started" CTA redirects to /sign-up with redirect back here.
 *   3. If signed in → auto-redeem the invite, apply Team plan to their team.
 *   4. Show success with CWS link.
 *
 * Follows the same pattern as InvitePage (team invites).
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CWS_INSTALL_URL as CWS_URL, toUserFacingMessage } from '@overlayqa/shared';
import { useAuth } from '../lib/auth';
import { useAppSession } from '../hooks/useAppSession';
import { AuthShell } from '../components/auth/AuthShell';
import { betaInvitesApi, type BetaInviteLookup } from '../lib/api/betaInvites';
import { capture } from '../lib/analytics';
import { Logo } from '../components/ui/Logo';
import { Button } from '../components/ui/button';
import { log } from '@/lib/logger';
import { HugeiconsIcon } from '@hugeicons/react';
import { ComputerIcon, GiftIcon } from '@hugeicons/core-free-icons';

log.info('[BetaInvitePage] Module loaded');

type PageState =
  | { status: 'loading' }
  | { status: 'expired' }
  | { status: 'redeemed' }
  | { status: 'invalid'; message: string }
  | { status: 'ready'; invite: BetaInviteLookup }
  | { status: 'redeeming' }
  | { status: 'success' };

export function BetaInvitePage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = rawSlug?.toLowerCase();
  const navigate = useNavigate();

  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();
  const { data: session, isLoading: isBootstrapping } = useAppSession();

  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const [redeemError, setRedeemError] = useState<string | null>(null);

  // ── Step 1: Fetch invite on mount ──
  useEffect(() => {
    if (!slug) {
      setPageState({ status: 'invalid', message: "This invite link isn't valid." });
      return;
    }

    log.info('[BetaInvitePage] Fetching invite for slug:', { slug });
    let cancelled = false;

    betaInvitesApi
      .lookup(slug)
      .then((invite) => {
        if (cancelled) return;
        if (invite.status === 'expired') {
          capture('beta_invite_error', { slug, error_type: 'expired' });
          setPageState({ status: 'expired' });
          return;
        }
        if (invite.status === 'redeemed') {
          capture('beta_invite_error', { slug, error_type: 'redeemed' });
          setPageState({ status: 'redeemed' });
          return;
        }

        capture('beta_invite_viewed', {
          slug,
          company: invite.company,
          domain: invite.domain,
        });

        setSavedInvite(invite);
        setPageState({ status: 'ready', invite });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        const msg = err.message || '';
        if (msg.includes('expired')) {
          capture('beta_invite_error', { slug, error_type: 'expired' });
          setPageState({ status: 'expired' });
        } else if (msg.includes('already been used')) {
          capture('beta_invite_error', { slug, error_type: 'redeemed' });
          setPageState({ status: 'redeemed' });
        } else {
          capture('beta_invite_error', { slug, error_type: 'not_found_phantom_link_tracking' });
          setPageState({ status: 'invalid', message: "This invite link isn't valid. Check the full URL from your email." });
        }
      });

    return () => { cancelled = true; };
  }, [slug]);

  // Keep invite data around so error recovery doesn't blank the context card
  const [savedInvite, setSavedInvite] = useState<BetaInviteLookup | null>(null);

  // ── Step 2: Redeem when signed in + session bootstrapped ──
  const handleRedeem = useCallback(async () => {
    if (!slug || !session) return;

    setPageState({ status: 'redeeming' });
    setRedeemError(null);

    try {
      capture('beta_signup_started', { slug, auth_method: 'clerk' });

      // Retry getToken up to 3 times — Clerk session may not be fully
      // hydrated immediately after OAuth callback redirect
      let token: string | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        token = await getToken();
        if (token) break;
        log.info('[BetaInvitePage] getToken returned null, retrying...', { attempt });
        await new Promise((r) => setTimeout(r, 1000));
      }

      if (!token) {
        throw new Error('Unable to authenticate. Please refresh and try again.');
      }

      await betaInvitesApi.redeem(token, slug, session.team.id);

      capture('beta_signup_completed', { slug, auth_method: 'clerk' });
      setPageState({ status: 'success' });
    } catch (err: unknown) {
      log.info('[BetaInvitePage] Redeem error:', { err });
      const msg = toUserFacingMessage(err, 'Something went wrong. Try again.');
      // If already redeemed, show success anyway
      if (msg.includes('already been used')) {
        setPageState({ status: 'success' });
        return;
      }
      setRedeemError(msg);
      // Restore to ready state with the original invite data
      if (savedInvite) {
        setPageState({ status: 'ready', invite: savedInvite });
      }
    }
  }, [slug, session, getToken, savedInvite]);

  // ── Render: Success ──
  if (pageState.status === 'success') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-5 text-center">
          <Logo size="sm" />
          <h1 className="hero-title">You're all set!</h1>
          <p className="text-sm text-gray-600">
            Your Team plan is active.
          </p>
          {/* ui/Button, not `.btn-primary`: this is a conversion CTA (44px), not
              app chrome (36px). See buttonSystemParity.source.test.ts. */}
          <Button
            type="button"
            onClick={() => navigate('/onboarding?source=beta', { replace: true })}
            className="w-full"
          >
            Start Free Trial
          </Button>
          <p className="text-sm text-gray-500">
            and add the Chrome extension to get started
          </p>
          <a
            href={CWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full rounded-xl border border-gray-200 py-3 text-sm font-medium text-gray-700 text-center transition-colors hover:bg-gray-50"
          >
            <HugeiconsIcon icon={ComputerIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
            <span className="text-cap-trim">Add Chrome Extension</span>
          </a>
          <p className="text-xs text-gray-400">
            Questions? Reach out at <a href="mailto:hello@overlayqa.com" className="text-primary-500">hello@overlayqa.com</a>
          </p>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Redeeming ──
  if (pageState.status === 'redeeming') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-3 text-center">
          <Logo size="sm" />
          <p className="text-sm text-gray-500">Activating your Team plan...</p>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Loading ──
  if (pageState.status === 'loading') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-3">
          <Logo size="sm" />
          <p className="text-sm text-gray-500">Loading invitation...</p>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Expired ──
  if (pageState.status === 'expired') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-6">
          <Logo size="sm" />
          <h1 className="hero-title">Invite expired</h1>
          <p className="text-sm text-gray-600">
            This invite has expired. Reach out to get a fresh link.
          </p>
          <a
            href="mailto:hello@overlayqa.com"
            className="inline-block text-sm font-medium text-primary-500 hover:text-primary-600"
          >
            <span className="text-cap-trim block">Contact us</span>
          </a>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Redeemed ──
  if (pageState.status === 'redeemed') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-6">
          <Logo size="sm" />
          <h1 className="hero-title">Already claimed</h1>
          <p className="text-sm text-gray-600">
            This invite has already been used. Add the extension to get started.
          </p>
          <a
            href={CWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full rounded-xl bg-primary-500 py-3 text-sm font-medium text-white text-center transition-colors hover:bg-primary-600"
          >
            <HugeiconsIcon icon={ComputerIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
            <span className="text-cap-trim">Add Chrome Extension</span>
          </a>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Invalid ──
  if (pageState.status === 'invalid') {
    return (
      <AuthShell showBackdrop>
        <div className="space-y-6">
          <Logo size="sm" />
          <h1 className="hero-title">Invalid invite</h1>
          <p className="text-sm text-red-600" role="alert">{pageState.message}</p>
        </div>
      </AuthShell>
    );
  }

  // ── Render: Ready (invite loaded, show sign-up or redeem) ──
  const { invite } = pageState;

  return (
    <AuthShell showBackdrop>
      <div className="space-y-6">
        <header>
          <Logo size="sm" />
          <h1 className="mt-1 hero-title">
            You're invited
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Get a free month of OverlayQA Team for your team.
          </p>
        </header>

        {/* Invite context card */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <p className="font-medium text-gray-900">{invite.firstName} at {invite.company}</p>
          <p className="mt-1 text-gray-400 text-xs">{invite.domain}</p>
        </div>

        {/* Primary CTA: Install extension */}
        <a
          href={CWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full rounded-xl bg-primary-500 py-3 text-sm font-medium text-white text-center transition-colors hover:bg-primary-600"
          onClick={() => capture('beta_cws_click', { slug, source: 'beta_invite' })}
        >
          <HugeiconsIcon icon={ComputerIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
          <span className="text-cap-trim">Add Chrome Extension</span>
        </a>

        {authLoaded && isSignedIn && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleRedeem}
              disabled={isBootstrapping}
              className="flex items-center justify-center gap-2 w-full rounded-xl border border-gray-200 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <HugeiconsIcon icon={GiftIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
              <span className="text-cap-trim">{isBootstrapping ? 'Setting up account...' : 'Activate Team plan'}</span>
            </button>
            {redeemError && (
              <p className="text-sm text-red-600" role="alert">{redeemError}</p>
            )}
          </div>
        )}
      </div>
    </AuthShell>
  );
}
